import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export interface CategoryOption {
	id: string;
	label: string;
	count: number;
}

interface HorizontalCategoryFilterProps {
	categories: CategoryOption[];
	activeCategory: string;
	onCategoryChange: (categoryId: string) => void;
	className?: string;
}

export const HorizontalCategoryFilter = ({
	categories,
	activeCategory,
	onCategoryChange,
	className,
}: HorizontalCategoryFilterProps) => {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(true);
	const [isDragging, setIsDragging] = useState(false);
	const [startX, setStartX] = useState(0);
	const [scrollLeft, setScrollLeft] = useState(0);
	const containerOffsetRef = useRef(0);

	const updateScrollButtons = useCallback(() => {
		requestAnimationFrame(() => {
			if (scrollContainerRef.current) {
				const container = scrollContainerRef.current;
				const scrollL = container.scrollLeft;
				const scrollW = container.scrollWidth;
				const clientW = container.clientWidth;
				setCanScrollLeft(scrollL > 5);
				setCanScrollRight(scrollL < scrollW - clientW - 5);
			}
		});
	}, []);

	useEffect(() => {
		const timeoutId = setTimeout(updateScrollButtons, 100);
		const container = scrollContainerRef.current;
		if (container) {
			container.addEventListener("scroll", updateScrollButtons, {
				passive: true,
			});
			return () => {
				clearTimeout(timeoutId);
				container.removeEventListener("scroll", updateScrollButtons);
			};
		}
		return () => clearTimeout(timeoutId);
	}, [updateScrollButtons]);

	const scrollFilters = (direction: "left" | "right") => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollBy({
				left: direction === "left" ? -200 : 200,
				behavior: "smooth",
			});
		}
	};

	const handleMouseDown = (e: React.MouseEvent) => {
		if (!scrollContainerRef.current) return;
		containerOffsetRef.current = scrollContainerRef.current.offsetLeft;
		setIsDragging(true);
		setStartX(e.pageX - containerOffsetRef.current);
		setScrollLeft(scrollContainerRef.current.scrollLeft);
	};

	const handleMouseMove = (e: React.MouseEvent) => {
		if (!isDragging || !scrollContainerRef.current) return;
		e.preventDefault();
		const x = e.pageX - containerOffsetRef.current;
		scrollContainerRef.current.scrollLeft = scrollLeft - (x - startX) * 1.5;
	};

	const handleMouseUp = () => setIsDragging(false);

	const handleTouchStart = (e: React.TouchEvent) => {
		if (!scrollContainerRef.current) return;
		containerOffsetRef.current = scrollContainerRef.current.offsetLeft;
		setStartX(e.touches[0].pageX - containerOffsetRef.current);
		setScrollLeft(scrollContainerRef.current.scrollLeft);
	};

	const handleTouchMove = (e: React.TouchEvent) => {
		if (!scrollContainerRef.current) return;
		const x = e.touches[0].pageX - containerOffsetRef.current;
		scrollContainerRef.current.scrollLeft = scrollLeft - (x - startX) * 1.5;
	};

	return (
		<div className={cn("relative", className)}>
			{canScrollLeft && (
				<div className="absolute left-0 top-0 bottom-0 w-5 bg-linear-to-r from-background/80 to-transparent z-10 pointer-events-none" />
			)}

			{/* Scrollable container */}
			<section
				ref={scrollContainerRef}
				aria-label="Filtro de categorias"
				onMouseDown={handleMouseDown}
				onMouseMove={handleMouseMove}
				onMouseUp={handleMouseUp}
				onMouseLeave={handleMouseUp}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				className={cn(
					"flex gap-2 overflow-x-auto scrollbar-hide select-none px-1",
					isDragging ? "cursor-grabbing" : "cursor-grab",
				)}
				style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
			>
				{categories.map((category) => (
					<button
						type="button"
						key={category.id}
						onClick={() => !isDragging && onCategoryChange(category.id)}
						className={cn(
							"px-5 py-3 rounded-full text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap shrink-0 min-h-12",
							activeCategory === category.id
								? "bg-primary text-primary-foreground"
								: "bg-muted text-muted-foreground hover:bg-muted/80",
						)}
					>
						{category.label}
						<span
							className={cn(
								"text-xs px-2.5 py-1 rounded-full",
								activeCategory === category.id
									? "bg-primary-foreground/20 text-primary-foreground"
									: "bg-muted-foreground/20 text-muted-foreground",
							)}
						>
							{category.count}
						</span>
					</button>
				))}
			</section>

			{canScrollRight && (
				<div className="absolute right-0 top-0 bottom-0 w-5 bg-linear-to-l from-background/80 to-transparent z-10 pointer-events-none" />
			)}

			{(canScrollLeft || canScrollRight) && (
				<div className="flex items-center justify-center gap-4 mt-4">
					<button
						type="button"
						onClick={() => scrollFilters("left")}
						disabled={!canScrollLeft}
						className="p-3 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 disabled:opacity-30 disabled:cursor-not-allowed transition-all min-w-12 min-h-12 flex items-center justify-center"
						aria-label="Rolar para esquerda"
					>
						<ChevronLeft className="h-5 w-5" />
					</button>
					<button
						type="button"
						onClick={() => scrollFilters("right")}
						disabled={!canScrollRight}
						className="p-3 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 disabled:opacity-30 disabled:cursor-not-allowed transition-all min-w-12 min-h-12 flex items-center justify-center"
						aria-label="Rolar para direita"
					>
						<ChevronRight className="h-5 w-5" />
					</button>
				</div>
			)}
		</div>
	);
};
